/**
 * Pagina /tesoreria/banco — Control de saldo bancario (Sprint 17 Fase 2).
 *
 * El tesorero registra periódicamente cuánto dinero hay en la cuenta
 * del barrio (según la aplicación bancaria, que viene en PDF con
 * clave y por eso se ingresa manual). El sistema calcula su saldo
 * esperado y muestra diferencias para conciliar.
 *
 * Arquitectura View + wrapper para soportar preview dev-only.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  getSaldoCalculadoHoy,
  listSaldosBancarios,
  type SaldoBancarioRead,
  type SaldoCalculadoHoyResponse,
} from '../../lib/api/saldos-bancarios';
import {
  getCuadreAnual,
  type CuadreAnualItem,
} from '../../lib/api/cierres-anuales';
import { formatCLP, formatFecha } from '../../lib/format';
import { Card } from '../../components/ui/Card';
import { ModalRegistrarSaldo } from '../../components/tesoreria/ModalRegistrarSaldo';
import { ModalRegistrarSaldoFinalBanco } from '../../components/tesoreria/ModalRegistrarSaldoFinalBanco';
import { ModalEjecutarCierreAnual } from '../../components/tesoreria/ModalEjecutarCierreAnual';
import { FondosTercerosCard } from '../../components/tesoreria/FondosTercerosCard';
import {
  getFondosTerceros,
  type ResumenFondosTerceros,
} from '../../lib/api/fondos-terceros';
import { LineChartSaldos } from '../../components/tesoreria/LineChartSaldos';
import { Monto, Num } from '../../components/tesoreria/Monto';
import { DepositosView } from '../../components/tesoreria/DepositosView';
import {
  listDepositos,
  crearDeposito,
  liquidarDeposito,
  type DepositoAPlazo,
  type CrearDepositoIn,
  type LiquidarDepositoIn,
} from '../../lib/api/depositos-a-plazo';

export interface BancoViewProps {
  calculadoHoy: SaldoCalculadoHoyResponse | null;
  historial: SaldoBancarioRead[];
  loading: boolean;
  error: string | null;
  onRegistrar: () => void;
  // Sprint 18 P16 + cuadre 2026-06: cierres anuales con el desglose del
  // flujo de caja (apertura → recaudación/gastos/DAP → cierre calculado)
  // y la diferencia contra el extracto declarado.
  cuadreAnual: CuadreAnualItem[];
  onEditarCierre: (c: CuadreAnualItem) => void;
  // Abre el modal de confirmación para ejecutar un cierre anual. Opcional
  // con default no-op para preview/tests que solo ejercitan el display.
  onEjecutarCierre?: () => void;
  // Fondos de terceros (campañas). Opcional: si no se pasa, la card no se
  // muestra (preview/tests del display la omiten).
  fondosTerceros?: ResumenFondosTerceros | null;
  // 2026-06 Bloque 2: tab Depósitos a Plazo (DAP). Opcionales con default
  // para no obligar a preview/tests que solo ejercitan el tab Banco.
  depositos?: DepositoAPlazo[];
  totalDepositos?: number;
  depositosLoading?: boolean;
  depositosError?: string | null;
  onCrearDeposito?: (data: CrearDepositoIn) => Promise<string | null>;
  onLiquidarDeposito?: (
    id: string,
    data: LiquidarDepositoIn,
  ) => Promise<string | null>;
}

type BancoTab = 'banco' | 'depositos';

/** Monto CLP o guion cuando el valor es null (apertura/cierre/diferencia
 *  sin datos). `className` aplica al monto (ej. color de la diferencia). */
function MontoODash({
  value,
  className,
}: {
  value: number | null;
  className?: string;
}) {
  return value !== null ? (
    <Monto value={value} className={className} />
  ) : (
    <span className="text-text-muted">—</span>
  );
}

export function BancoView({
  calculadoHoy,
  historial,
  loading,
  error,
  onRegistrar,
  cuadreAnual,
  onEditarCierre,
  onEjecutarCierre = () => undefined,
  fondosTerceros = null,
  depositos = [],
  totalDepositos = 0,
  depositosLoading = false,
  depositosError = null,
  onCrearDeposito = async () => null,
  onLiquidarDeposito = async () => null,
}: BancoViewProps) {
  const [tab, setTab] = useState<BancoTab>('banco');
  const ultimo = historial[0];
  // Modelo independiente: la diferencia es banco − sistema (reconstruido
  // desde la apertura hasta hoy), coherente con las dos tarjetas vecinas.
  const dif = calculadoHoy?.diferencia ?? null;

  // Fila provisional del año EN CURSO para la tabla de cuadre anual: mismo
  // desglose que un cierre (apertura + recaudación − gastos − DAP), pero sin
  // cerrar. El "extracto declarado" es el último saldo registrado del banco.
  // Solo si hay apertura sembrada y el año aún no está cerrado (no duplicar).
  const cuadreEnCurso =
    calculadoHoy &&
    !calculadoHoy.sin_apertura &&
    !cuadreAnual.some((c) => c.anio === calculadoHoy.anio)
      ? {
          anio: calculadoHoy.anio,
          apertura: calculadoHoy.apertura,
          recaudacion: calculadoHoy.ingresos_anio,
          gastos: calculadoHoy.gastos_anio,
          traspasos_dap: calculadoHoy.traspasos_dap_anio,
          cierre_calculado: calculadoHoy.saldo_calculado,
          saldo_final_banco: calculadoHoy.ultimo_registro_saldo_banco,
          diferencia: calculadoHoy.diferencia,
        }
      : null;

  return (
    <div className="px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Control de saldo bancario
          </h1>
          <p className="text-sm text-text-secondary mt-1 max-w-2xl">
            Registra el saldo del banco para conciliar con el cálculo del
            sistema. Diferencias indican movimientos no registrados.
          </p>
        </div>
        {tab === 'banco' && (
          <button
            type="button"
            onClick={onRegistrar}
            className="bg-orange text-white px-4 py-2 rounded text-sm font-medium hover:opacity-90"
          >
            + Registrar saldo
          </button>
        )}
      </div>

      {/* Tabs Banco | Depósitos (2026-06 Bloque 2) */}
      <div className="flex gap-1 border-b border-white/[0.08]">
        {(['banco', 'depositos'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
              tab === t
                ? 'border-orange text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            }`}
          >
            {t === 'banco' ? 'Banco' : 'Depósitos'}
          </button>
        ))}
      </div>

      {tab === 'depositos' && (
        <DepositosView
          depositos={depositos}
          totalVigente={totalDepositos}
          saldoCorriente={ultimo?.saldo_banco ?? null}
          loading={depositosLoading}
          error={depositosError}
          onCrear={onCrearDeposito}
          onLiquidar={onLiquidarDeposito}
        />
      )}

      {tab === 'banco' && (
        <>
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Cards resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Saldo según banco
            </div>
            <div className="text-2xl font-medium text-text-primary mt-2">
              {loading
                ? '—'
                : ultimo
                  ? <Monto value={ultimo.saldo_banco} />
                  : 'Sin registros'}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {ultimo
                ? `${formatFecha(ultimo.fecha_registro)} · ${ultimo.hora_registro.slice(0, 5)}`
                : 'Registra el primer saldo para empezar.'}
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Saldo calculado por sistema
            </div>
            <div className="text-2xl font-medium text-text-primary mt-2">
              {loading
                ? '—'
                : calculadoHoy?.sin_apertura
                  ? '—'
                  : <Monto value={calculadoHoy?.saldo_calculado ?? 0} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {calculadoHoy?.sin_apertura
                ? 'Siembra el saldo de apertura del año.'
                : 'Apertura del año + ingresos − gastos − depósitos, a hoy.'}
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Diferencia
            </div>
            <div
              className={`text-2xl font-medium mt-2 ${
                dif === 0 ? 'text-emerald-400' : 'text-amber-400'
              }`}
            >
              {loading
                ? '—'
                : dif === null
                  ? '—'
                  : <Num>{dif >= 0 ? '+' : ''}{formatCLP(dif)}</Num>}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {dif === null
                ? calculadoHoy?.sin_apertura
                  ? 'Requiere el saldo de apertura del año.'
                  : 'Registra el saldo del banco para comparar.'
                : dif === 0
                  ? 'Cuadra. ✓'
                  : dif > 0
                    ? 'Plata en el banco sin registrar en el sistema.'
                    : 'El sistema registra más de lo que hay en el banco.'}
            </div>
          </div>
        </Card>
      </div>

      {/* Gráfico evolución */}
      <Card>
        <div className="p-4">
          <LineChartSaldos historial={historial} />
        </div>
      </Card>

      {/* Tabla historial */}
      <Card>
        <div className="p-4">
          <div className="text-sm font-medium text-text-primary mb-3">
            Historial de registros
          </div>
          {loading && (
            <div className="text-sm text-text-muted">Cargando…</div>
          )}
          {!loading && historial.length === 0 && (
            <div className="text-sm text-text-muted">
              Sin registros aún. Usa "+ Registrar saldo" para comenzar.
            </div>
          )}
          {!loading && historial.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                    <th className="text-left font-medium py-2">Fecha · Hora</th>
                    <th className="text-right font-medium py-2">Banco</th>
                    <th className="text-right font-medium py-2">Sistema</th>
                    <th className="text-right font-medium py-2">Diferencia</th>
                    <th className="text-left font-medium py-2 pl-3">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className="py-2 text-text-secondary">
                        {formatFecha(s.fecha_registro)} ·{' '}
                        {s.hora_registro.slice(0, 5)}
                      </td>
                      <td className="py-2 text-right text-text-primary">
                        <Monto value={s.saldo_banco} />
                      </td>
                      <td className="py-2 text-right text-text-secondary">
                        <Monto value={s.saldo_calculado} />
                      </td>
                      <td
                        className={`py-2 text-right font-medium ${
                          s.diferencia === 0
                            ? 'text-emerald-400'
                            : 'text-amber-400'
                        }`}
                      >
                        {s.diferencia === 0
                          ? <Monto value={0} />
                          : <Num>{s.diferencia >= 0 ? '+' : ''}{formatCLP(s.diferencia)}</Num>}
                      </td>
                      <td className="py-2 pl-3 text-text-muted truncate max-w-xs">
                        {s.notas || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Cuadre anual de flujo de caja (2026-06). Deja constancia año a
          año: apertura (= saldo final del año anterior) + recaudación −
          gastos − traspasos DAP = cierre calculado, contra el extracto
          declarado. Reemplaza la tabla "Cierres anuales — saldo bancario"
          (Sprint 18 P16) absorbiendo aquí el ítem "Cierres" del menú. */}
      <Card>
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-lg font-medium text-text-primary">
            Cuadre anual de flujo de caja
          </h2>
          <button
            type="button"
            onClick={onEjecutarCierre}
            className="text-xs text-orange hover:underline whitespace-nowrap shrink-0"
          >
            Ejecutar cierre anual
          </button>
        </div>
        <p className="text-sm text-text-secondary mb-4">
          Apertura (saldo final del año anterior) + recaudación − gastos −
          traspasos a depósito = cierre calculado, comparado con el saldo del
          banco. La primera fila es el <strong>año en curso</strong> (provisional,
          al día de hoy); las siguientes son los años ya cerrados (contra el
          extracto de diciembre). Una diferencia distinta de cero puede deberse
          a un descuadre real o a un pago/gasto cargado en el año equivocado.
        </p>
        {cuadreAnual.length === 0 && !cuadreEnCurso ? (
          <p className="text-sm text-text-muted">
            Aún no hay cierres anuales ejecutados.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.08] text-left text-xs text-text-muted">
                  <th className="py-2 pr-3">Año</th>
                  <th className="py-2 pr-3 text-right">Apertura</th>
                  <th className="py-2 pr-3 text-right">+ Recaudación</th>
                  <th className="py-2 pr-3 text-right">− Gastos</th>
                  <th className="py-2 pr-3 text-right">− Traspasos DAP</th>
                  <th className="py-2 pr-3 text-right">= Cierre calculado</th>
                  <th className="py-2 pr-3 text-right">Extracto declarado</th>
                  <th className="py-2 pr-3 text-right">Diferencia</th>
                  <th className="py-2 pl-3"></th>
                </tr>
              </thead>
              <tbody>
                {cuadreEnCurso && (
                  <tr className="border-b border-white/[0.04] bg-orange/[0.04]">
                    <td className="py-2 pr-3 font-medium">
                      <Num>{cuadreEnCurso.anio}</Num>
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-orange">
                        en curso
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <MontoODash value={cuadreEnCurso.apertura} />
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <Monto value={cuadreEnCurso.recaudacion} />
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <Monto value={cuadreEnCurso.gastos} />
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <Monto value={cuadreEnCurso.traspasos_dap} />
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      <MontoODash value={cuadreEnCurso.cierre_calculado} />
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      <MontoODash value={cuadreEnCurso.saldo_final_banco} />
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      <MontoODash
                        value={cuadreEnCurso.diferencia}
                        className={
                          cuadreEnCurso.diferencia === 0
                            ? 'text-emerald-400'
                            : 'text-red-400'
                        }
                      />
                    </td>
                    <td className="py-2 pl-3 text-[10px] text-text-muted">
                      provisional
                    </td>
                  </tr>
                )}
                {cuadreAnual.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-white/[0.04]"
                  >
                    <td className="py-2 pr-3 font-medium"><Num>{c.anio}</Num></td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <MontoODash value={c.apertura} />
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <Monto value={c.recaudacion} />
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <Monto value={c.gastos} />
                    </td>
                    <td className="py-2 pr-3 text-right text-text-secondary">
                      <Monto value={c.traspasos_dap} />
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      <MontoODash value={c.cierre_calculado} />
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      <MontoODash value={c.saldo_final_banco} />
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      <MontoODash
                        value={c.diferencia}
                        className={
                          c.diferencia === 0
                            ? 'text-emerald-400'
                            : 'text-red-400'
                        }
                      />
                    </td>
                    <td className="py-2 pl-3">
                      <button
                        type="button"
                        onClick={() => onEditarCierre(c)}
                        className="text-xs text-orange hover:underline"
                      >
                        {c.saldo_final_banco !== null
                          ? 'Editar'
                          : 'Registrar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Fondos de terceros (campañas muni/JJVV): solo aparece si hay
          categorías de terceros con movimientos. */}
      <FondosTercerosCard data={fondosTerceros} />
        </>
      )}
    </div>
  );
}

export function BancoPage() {
  const [calculadoHoy, setCalculadoHoy] =
    useState<SaldoCalculadoHoyResponse | null>(null);
  const [historial, setHistorial] = useState<SaldoBancarioRead[]>([]);
  const [cuadreAnual, setCuadreAnual] = useState<CuadreAnualItem[]>([]);
  const [fondosTerceros, setFondosTerceros] =
    useState<ResumenFondosTerceros | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ejecutarModalOpen, setEjecutarModalOpen] = useState(false);
  const [cierreEditando, setCierreEditando] = useState<CuadreAnualItem | null>(
    null,
  );
  // 2026-06 Bloque 2: depósitos a plazo.
  const [depositos, setDepositos] = useState<DepositoAPlazo[]>([]);
  const [totalDepositos, setTotalDepositos] = useState(0);
  const [depositosLoading, setDepositosLoading] = useState(true);
  const [depositosError, setDepositosError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [calc, hist, cuadre, terceros] = await Promise.all([
        getSaldoCalculadoHoy(),
        listSaldosBancarios(50, 0),
        getCuadreAnual(),
        getFondosTerceros(),
      ]);
      setCalculadoHoy(calc);
      setHistorial(hist);
      setCuadreAnual(cuadre);
      setFondosTerceros(terceros);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDepositos = useCallback(async () => {
    setDepositosLoading(true);
    setDepositosError(null);
    try {
      const r = await listDepositos();
      setDepositos(r.depositos);
      setTotalDepositos(r.total_vigente);
    } catch (e) {
      setDepositosError(
        e instanceof Error ? e.message : 'Error al cargar depósitos',
      );
    } finally {
      setDepositosLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadDepositos();
  }, [load, loadDepositos]);

  async function handleCrearDeposito(
    data: CrearDepositoIn,
  ): Promise<string | null> {
    try {
      await crearDeposito(data);
      await Promise.all([loadDepositos(), load()]); // refresca DAPs + conciliación
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Error al registrar el depósito';
    }
  }

  async function handleLiquidarDeposito(
    id: string,
    data: LiquidarDepositoIn,
  ): Promise<string | null> {
    try {
      await liquidarDeposito(id, data);
      await Promise.all([loadDepositos(), load()]);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Error al liquidar el depósito';
    }
  }

  return (
    <>
      <BancoView
        calculadoHoy={calculadoHoy}
        historial={historial}
        loading={loading}
        error={error}
        onRegistrar={() => setModalOpen(true)}
        cuadreAnual={cuadreAnual}
        onEditarCierre={setCierreEditando}
        onEjecutarCierre={() => setEjecutarModalOpen(true)}
        fondosTerceros={fondosTerceros}
        depositos={depositos}
        totalDepositos={totalDepositos}
        depositosLoading={depositosLoading}
        depositosError={depositosError}
        onCrearDeposito={handleCrearDeposito}
        onLiquidarDeposito={handleLiquidarDeposito}
      />
      <ModalRegistrarSaldo
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => load()}
      />
      {cierreEditando && (
        <ModalRegistrarSaldoFinalBanco
          cierre={cierreEditando}
          onCerrar={() => setCierreEditando(null)}
          onGuardado={() => {
            setCierreEditando(null);
            load();
          }}
        />
      )}
      {ejecutarModalOpen && (
        <ModalEjecutarCierreAnual
          onCerrar={() => setEjecutarModalOpen(false)}
          onEjecutado={() => {
            setEjecutarModalOpen(false);
            load();
          }}
        />
      )}
    </>
  );
}
